import { ContactFlyout } from "@/components/app-shell/sidebar/contact-flyout";
import {
  BetaFeatureInsetScope,
  BetaFeatureOnly,
} from "@/components/beta-feature";
import { NewChip } from "@/components/common/new-chip";
import { pushIntegrationGuideModal } from "@/components/modals/ixd-match-integration-guide-modal";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/constants/terms";
import { USER_GUIDE_HOME_URL } from "@/constants/user-guide";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { useGuideModalStore } from "@/stores/guide-modal";
import { Popover, PopoverContent, PopoverTrigger } from "@/styles/ui/popover";
import Avatar from "@/toggle-ui-kit/avatar";
import DropdownListItem from "@/toggle-ui-kit/dropdown-list-item";
import {
  ViewSwitchGroup,
  type ViewSwitchOption,
} from "@/toggle-ui-kit/view-switch-group";
import { openInNewTab } from "@/utils/open-in-new-tab";
import {
  logExternalServiceOpen,
  logHomeServiceAccess,
  logInvectorAiOpen,
  logSolutionTabChange,
  logUserGuideOpen,
} from "@/utils/usage-log";
import {
  ChevronDown,
  CircleUser,
  ClipboardCheck,
  ClipboardPaste,
  ExternalLink,
  FilePen,
  FileSpreadsheet,
  FileText,
  Home,
  Inbox,
  ListChecks,
  LogOut,
  MoveDiagonal2,
  MoveHorizontal,
  Repeat,
  SearchCheck,
  Type,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Fragment, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const DETAIL_TAGGING_URL =
  "https://xbrl-detail-tagging-70246556166.asia-northeast3.run.app";

type MenuItem = {
  label: string;
  icon: LucideIcon;
  isNew?: boolean;
  /** 베타 기능을 켠 사람에게만 보인다 */
  isBeta?: boolean;
} & (
  | { to: string; external?: boolean; action?: never }
  // 이동하지 않고 그 자리에서 안내만 띄우는 메뉴
  | { action: () => void; to?: never; external?: never }
);

type Solution = "dsd" | "xbrl";

const MENUS: Record<Solution, { category: string; items: MenuItem[] }[]> = {
  dsd: [
    {
      category: "보고서 준비",
      items: [
        { label: "DSD 엑셀 변환", icon: Repeat, to: "/dsd-xlsx" },
        {
          label: "정산표 DSD 변환",
          icon: FileSpreadsheet,
          to: "/accounting-sheet-dsd",
        },
      ],
    },
    {
      category: "보고서 검토",
      items: [
        { label: "DSD 리뷰", icon: SearchCheck, to: "/dsd-editor" },
        {
          label: "DSD - DSD 비교",
          icon: ClipboardCheck,
          to: "/compare/dsd-dsd",
        },
      ],
    },
  ],
  xbrl: [
    {
      category: "보고서 준비",
      items: [{ label: "DSD 워크북 관리", icon: Inbox, to: "/workbooks" }],
    },
    {
      category: "보고서 작성",
      items: [
        {
          label: "IXD 값 편집기",
          icon: FilePen,
          to: "/ixd-editor",
          // TODO(WXBRL-2659): 출시 안내 기간(~2026-11-15)이 끝나면 isNew 를 제거한다.
          // 레거시 사이드바(menu-container.tsx 의 IxdEditorMenu)에도 NewChip 이 고정으로
          // 붙어 있으므로 라벨을 내릴 때 두 곳을 함께 정리한다.
          isNew: true,
        },
        {
          label: "DSD 워크북 - IXD 값 채우기",
          icon: ClipboardPaste,
          // IXD 값 편집기로 통합됐다 — 누르면 이동하지 않고 안내만 띄운다
          action: pushIntegrationGuideModal,
        },
        {
          label: "문장 영역 디테일 태깅",
          icon: Type,
          to: DETAIL_TAGGING_URL,
          external: true,
        },
      ],
    },
    {
      category: "보고서 검토",
      items: [
        {
          label: "DSD - IXD 비교",
          icon: MoveDiagonal2,
          to: "/ixd-dsd-compare",
        },
        {
          label: "IXD - IXD 비교",
          icon: MoveHorizontal,
          to: "/ixd-preview-compare",
        },
        {
          label: "IXD 주석 검증",
          icon: ListChecks,
          to: "/ixd-note-checklist",
          isBeta: true,
        },
      ],
    },
  ],
};

const HOME: MenuItem = { label: "홈", icon: Home, to: "/" };

const SOLUTION_OPTIONS: [
  ViewSwitchOption<Solution>,
  ViewSwitchOption<Solution>,
] = [
  { value: "dsd", label: "DSD 솔루션" },
  { value: "xbrl", label: "XBRL 솔루션" },
];

const isActive = (item: MenuItem, pathname: string) =>
  !item.external &&
  !!item.to &&
  (pathname === item.to || pathname.startsWith(`${item.to}/`));

// 홈처럼 어느 솔루션에도 속하지 않는 화면에서 되살릴, 마지막으로 본 솔루션.
// 새로고침은 넘기고 탭끼리는 섞이지 않아야 하므로 sessionStorage 에 둔다
const SOLUTION_KEY = "lsbSolution";

// 경로 매칭 순서 — xbrl 먼저
const SOLUTIONS = ["xbrl", "dsd"] as const satisfies readonly Solution[];

const hasActiveMenu = (solution: Solution, pathname: string) =>
  MENUS[solution].some((part) =>
    part.items.some((item) => isActive(item, pathname)),
  );

// 사이드바는 페이지마다 새로 마운트되므로, 탭은 현재 페이지가 속한 솔루션에서 시작한다.
// 홈처럼 메뉴가 없는 화면은 마지막으로 본 솔루션으로 돌아간다.
// 경로로 정해졌으면 그 자리에서 기억까지 한다 — 토글을 누른 적 없이 URL 로 바로 들어온
// 사용자도 홈으로 가면 이 솔루션으로 돌아와야 하고, 그 저장은 이벤트가 아니라 마운트다
const openSolutionAt = (pathname: string): Solution => {
  const fromPath = SOLUTIONS.find((it) => hasActiveMenu(it, pathname));
  if (fromPath) {
    sessionStorage.setItem(SOLUTION_KEY, fromPath);
    return fromPath;
  }
  // 저장값은 믿지 않는다 — "xbrl" 이 아니면 전부 기본값으로 떨군다
  return sessionStorage.getItem(SOLUTION_KEY) === "xbrl" ? "xbrl" : "dsd";
};

const Divider = () => <div className="h-px w-full shrink-0 bg-line-normal" />;

const FLYOUT_ITEM = "rounded-md !label-1-normal-medium !text-label-normal";

const ProfileMenu = () => {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    const result = await logout();
    if (!result.success) {
      console.error("로그아웃 실패:", result.error);
      toast.error("로그아웃에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    // TODO(홈 개편): toggle-ui-kit 에 Popover 가 생기면 이관한다 (@/styles/ui 는 deprecated)
    <Popover>
      {/* 배경은 래퍼가 깔고, 버튼은 line-neutral 을 얹는 오버레이만 담당한다.
          트리거는 버튼이어야 data-state 가 버튼에 붙어 열린 동안 hover 모양이 유지된다 */}
      <div className="shrink-0 rounded-xl bg-gray-200">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="프로필 메뉴"
            className="flex w-full items-center gap-4 rounded-xl p-4 text-left hover:bg-line-neutral data-[state=open]:bg-line-neutral"
          >
            <Avatar
              displayName={user?.displayName ?? undefined}
              email={user?.email ?? ""}
              size="40"
              stroke="white"
              type="label"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="line-clamp-2 break-all label-2-medium text-label-alternative">
                {user?.company?.companyName}
              </span>
              <span className="line-clamp-2 break-all body-1-normal-medium text-neutral-950">
                {user?.displayName}
              </span>
            </div>
            <ChevronDown className="size-5 shrink-0 text-label-normal" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="flex w-[var(--radix-popover-trigger-width)] flex-col gap-1 rounded-2xl border-neutral-200 px-0 py-2 shadow-xlarge"
        side="bottom"
        align="start"
        sideOffset={4}
      >
        <p className="truncate px-4 py-1.5 caption-1-medium text-label-alternative">
          {user?.email}
        </p>
        <Divider />
        <div className="flex flex-col gap-1 px-1">
          <DropdownListItem
            className={FLYOUT_ITEM}
            leftIcon={<FileText className="size-3.5" />}
            rightIcon={<ExternalLink className="size-3.5" />}
            onClick={() => openInNewTab(TERMS_OF_SERVICE_URL)}
          >
            서비스 이용약관
          </DropdownListItem>
          <DropdownListItem
            className={FLYOUT_ITEM}
            leftIcon={<CircleUser className="size-3.5" />}
            rightIcon={<ExternalLink className="size-3.5" />}
            onClick={() => openInNewTab(PRIVACY_POLICY_URL)}
          >
            개인정보처리방침
          </DropdownListItem>
        </div>
        <Divider />
        <div className="flex flex-col px-1">
          <DropdownListItem
            className={FLYOUT_ITEM}
            leftIcon={<LogOut className="size-3.5" />}
            onClick={handleLogout}
          >
            로그아웃
          </DropdownListItem>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const menuClass = (active?: boolean) =>
  cn(
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-blue-500/8",
    active && "bg-blue-500/8",
  );

const MenuBody = ({ icon: Icon, label, isNew }: MenuItem) => (
  <>
    <Icon className="size-5 shrink-0 text-label-normal" />
    <span
      className={cn(
        "truncate body-2-normal-medium text-label-normal",
        !isNew && "flex-1",
      )}
    >
      {label}
    </span>
    {isNew && <NewChip color="indigo" className="bg-indigo-200" />}
  </>
);

// 새 탭이 열리면 앱 탭에는 mouseout 이 오지 않아 포인터가 이 메뉴 위에 머문 채 hover 배경이
// 남는다. 그 색이 선택 색과 같아 앱으로 돌아왔을 때 메뉴 두 개가 선택된 것처럼 보이므로,
// 클릭한 순간 hover 를 끄고 포인터가 다시 움직일 때 되살린다
// (mouseenter 는 포인터가 이미 안에 있어 다시 오지 않으므로 mousemove 로 푼다)
const ExternalMenuLink = ({
  item,
  onClick,
}: {
  item: MenuItem;
  onClick?: () => void;
}) => {
  const [hoverSuppressed, setHoverSuppressed] = useState(false);

  return (
    <a
      href={item.to}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        setHoverSuppressed(true);
        onClick?.();
      }}
      onMouseMove={() => setHoverSuppressed(false)}
      className={cn(menuClass(), hoverSuppressed && "hover:bg-transparent")}
    >
      <MenuBody {...item} />
    </a>
  );
};

// 링크로 렌더해야 휠 클릭/Cmd+클릭 새 탭, "링크 주소 복사"가 동작한다
const MenuLink = ({
  item,
  active,
  onClick,
}: {
  item: MenuItem;
  active?: boolean;
  onClick?: () => void;
}) =>
  item.action ? (
    <button
      type="button"
      className={menuClass(active)}
      onClick={() => {
        onClick?.();
        item.action?.();
      }}
    >
      <MenuBody {...item} />
    </button>
  ) : item.external ? (
    <ExternalMenuLink item={item} onClick={onClick} />
  ) : (
    <Link
      to={item.to}
      aria-current={active ? "page" : undefined}
      className={menuClass(active)}
      onClick={onClick}
    >
      <MenuBody {...item} />
    </Link>
  );

export const HomeLSB = () => {
  const { pathname } = useLocation();
  const [solution, setSolution] = useState(() => openSolutionAt(pathname));
  // 홈 안내 모달(features/home)을 닫은 뒤로는 이 배지가 안내를 대신한다
  const { dismissed: guideDismissed } = useGuideModalStore();
  const logMenuClick = (item: MenuItem) => {
    // 외부 서비스는 쓴 흔적이 이 클릭뿐이라 어느 화면에서 눌렀든 센다
    if (item.external) logExternalServiceOpen(item.label);
    // 사이드바는 모든 화면에 있지만, 로그 이름이 "홈에서" 이므로 홈에서 누른 것만 센다
    if (pathname === HOME.to) logHomeServiceAccess("사이드바", item.label);
  };

  return (
    // 메뉴 목록이 overflow-y-auto 라 바깥 outline 이 잘린다 — 베타 표식을 요소 안쪽에 그린다 (레거시 사이드바와 같은 처리)
    <BetaFeatureInsetScope>
      <nav className="flex w-[320px] self-stretch shrink-0 flex-col bg-white px-3 py-4">
        <div className="flex min-h-0 flex-1 flex-col gap-6 rounded-[24px] bg-background-gray-alternative px-4 pb-4 pt-6">
          <div className="px-2">
            {/* inline-flex: 인라인 a 의 baseline 여백을 지워 클릭/포커스 박스를 로고에 딱 맞춘다 */}
            <Link to={HOME.to} aria-label="홈으로 이동" className="inline-flex">
              <img
                className="h-[14px] w-[74.587px]"
                src="/login-assets/invector-logo.svg"
                alt=""
              />
            </Link>
          </div>

          <ProfileMenu />

          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
            <MenuLink item={HOME} active={pathname === "/"} />

            <div className="flex flex-col gap-3">
              {/* 트랙 색(gray-200)과 가로 꽉 채움만 디자인에 맞춰 덮어쓴다 */}
              <ViewSwitchGroup
                size="32px"
                className="flex w-full shrink-0 bg-gray-200 p-1 [&>button]:flex-1"
                options={SOLUTION_OPTIONS}
                value={solution}
                onChange={(next) => {
                  const option = SOLUTION_OPTIONS.find((o) => o.value === next);
                  if (option) logSolutionTabChange(option.label);
                  setSolution(next);
                  sessionStorage.setItem(SOLUTION_KEY, next);
                }}
              />

              {MENUS[solution].map((part, i) => (
                <Fragment key={part.category}>
                  {i > 0 && <Divider />}
                  <div className="flex flex-col gap-1">
                    <span className="truncate px-2 py-1 caption-1-semibold text-label-alternative">
                      {part.category}
                    </span>
                    <div className="flex flex-col gap-0.5">
                      {part.items.map((item) => {
                        const link = (
                          <MenuLink
                            key={item.label}
                            item={item}
                            active={isActive(item, pathname)}
                            onClick={() => logMenuClick(item)}
                          />
                        );
                        return item.isBeta ? (
                          <BetaFeatureOnly key={item.label}>
                            {link}
                          </BetaFeatureOnly>
                        ) : (
                          link
                        );
                      })}
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-2 rounded-xl bg-gray-200 p-2">
            <div className="flex flex-col gap-1">
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-line-neutral"
                onClick={() => {
                  logUserGuideOpen("사이드바");
                  openInNewTab(USER_GUIDE_HOME_URL);
                }}
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center label-1-normal-medium text-label-neutral">
                    이용가이드
                    {guideDismissed && (
                      <img
                        className="size-4"
                        src="/icons/guide-badge.svg"
                        alt=""
                      />
                    )}
                  </span>
                  <span className="caption-1-regular text-label-alternative">
                    기능 사용법을 언제든 확인하세요
                  </span>
                </div>
                <ExternalLink className="size-3.5 shrink-0 text-label-neutral" />
              </button>
              <div className="px-2">
                <Divider />
              </div>
              <ContactFlyout />
            </div>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-white px-2 py-3 text-left"
              onClick={() => {
                logInvectorAiOpen("사이드바");
                openInNewTab("/help");
              }}
            >
              <img
                className="size-6 shrink-0"
                src="/icons/invector-ai-bot.svg"
                alt=""
              />
              <p className="flex-1 caption-1-regular text-label-neutral">
                <span className="caption-1-semibold text-blue-500">
                  인벡터 AI
                </span>
                에게 먼저 물어보세요!
                <br />
                기다림 없이 바로 해결하실 수 있습니다.
              </p>
            </button>
          </div>
        </div>
      </nav>
    </BetaFeatureInsetScope>
  );
};
